import { useEffect, useRef } from 'react'
import { useCrewActivity } from '../../hooks/useCrewActivity'
import { useCrewActivityUnread } from '../../hooks/useCrewActivityUnread'
import { useTabActive, useVisitState } from '../../hooks/useTabVisit'
import { useOpenEvent } from '../../hooks/useOpenEvent'
import { useUserEventInterests } from '../../hooks/useUserEventInterests'
import { useAuth } from '../../hooks/useAuth'
import { useIncrementalReveal } from '../../hooks/useIncrementalReveal'
import { placeholders } from '../../lib/skeletonUtils'
import { Section } from '../../components/Section'
import { EmptyState } from '../../components/EmptyState'
import { Skeleton } from '../../components/ui/skeleton'
import { fbt, useLocaleContext } from 'fbtee'
import { CalendarDays } from 'lucide-react'
import { beaconUserFallback } from '../../lib/commonStrings'
import { formatRelativeTime } from '../../lib/formatDate'
import type { CrewActivity } from '../../hooks/useCrewActivity'
import type { EventInterestStatus } from '../../hooks/useUserEventInterests'
import type { UserProfile } from '../../types'
import { ThumbStack } from '../../components/profile/ThumbStack'
import { UserAvatar } from '../../components/UserAvatar'
import { EventAttendanceMenu } from '../../components/EventAttendanceMenu'

function rsvpLead(status: 'going' | 'interested', ids: string[], nameOf: (uid: string) => string): string {
  const name = nameOf(ids[0])
  const others = ids.length - 1
  if (status === 'going') {
    return others > 0
      ? fbt(
          fbt.param('name', name) + ' and ' + fbt.param('count', others) + ' more are going',
          'Crew activity, under the event name: a friend and others marked going',
        )
      : fbt(fbt.param('name', name) + ' is going', 'Crew activity, under the event name: a friend marked going')
  }
  return others > 0
    ? fbt(
        fbt.param('name', name) + ' and ' + fbt.param('count', others) + ' more are interested',
        'Crew activity, under the event name: a friend and others marked interested',
      )
    : fbt(fbt.param('name', name) + ' is interested', 'Crew activity, under the event name: a friend marked interested')
}

function linkedLead({ groups }: CrewActivity, nameOf: (uid: string) => string): string {
  const [{ group, userId }] = groups
  const others = groups.length - 1
  return others > 0
    ? fbt(
        fbt.param('name', nameOf(userId)) + ' added it to ' + fbt.param('group', group.name) + ' and ' +
          fbt.plural('more group', others, { showCount: 'yes', many: 'more groups' }),
        'Crew activity, under the event name: who linked this event to several of your groups',
      )
    : fbt(
        fbt.param('name', nameOf(userId)) + ' added it to ' + fbt.param('group', group.name),
        'Crew activity, under the event name: who linked this event to which of your groups',
      )
}

/** What happened on the event: who's going (else interested), with how many more are interested; who linked it only when nobody RSVP'd. */
function activityLine(item: CrewActivity, nameOf: (uid: string) => string): string[] {
  const { goingIds, interestedIds } = item
  if (goingIds.length === 0 && interestedIds.length === 0) return [linkedLead(item, nameOf)]
  const parts = goingIds.length > 0 ? [rsvpLead('going', goingIds, nameOf)] : [rsvpLead('interested', interestedIds, nameOf)]
  if (goingIds.length > 0 && interestedIds.length > 0) {
    parts.push(fbt(
      fbt.param('count', interestedIds.length) + ' interested',
      'Crew activity, after who is going: how many more friends marked interested in the same event',
    ))
  }
  return parts
}

/** One event the crew touched: its photo with who did it, what happened, when, and your own RSVP. */
function CrewActivityRow({
  item,
  profiles,
  myStatus,
  unread,
  onOpen,
  onSetStatus,
}: {
  item: CrewActivity
  unread: boolean
  profiles: Record<string, UserProfile>
  myStatus: EventInterestStatus
  onOpen: () => void
  onSetStatus: (status: EventInterestStatus) => void
}) {
  const { locale } = useLocaleContext()
  const nameOf = (uid: string) => String(profiles[uid]?.displayName?.trim() || beaconUserFallback())
  const actor = item.goingIds[0] ?? item.interestedIds[0] ?? item.groups[0].userId

  return (
    <li className="flex items-center gap-3" data-testid="crew-activity-row" data-unread={unread || undefined}>
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left">
        <span className="relative shrink-0">
          <ThumbStack srcs={[item.event.imageUrl]} icon={CalendarDays} size="md" />
          <UserAvatar
            profile={profiles[actor]}
            fallbackName={nameOf(actor)}
            size="xs"
            className="absolute -bottom-1 -right-1 z-10 ring-2 ring-background"
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            {unread && (
              <span className="size-2 shrink-0 rounded-full bg-primary" data-testid="crew-activity-unread-dot">
                <span className="sr-only">{fbt('New', 'Crew activity: screen-reader label on the dot marking a row as new since your last visit')}</span>
              </span>
            )}
            <span className="truncate text-sm font-medium">{item.event.name}</span>
          </span>
          <span className="flex gap-1 text-xs text-muted-foreground">
            <span className="truncate">{activityLine(item, nameOf).join(' · ')}</span>
            <span className="shrink-0">· {formatRelativeTime(new Date(item.at), locale)}</span>
          </span>
        </span>
      </button>
      <EventAttendanceMenu variant="icon" status={myStatus} onSetStatus={onSetStatus} />
    </li>
  )
}

/** "What's new with your crew" — the recent plans of your friends and groups, each one RSVP-able in a tap. */
export function CrewActivitySection() {
  const { signedInUid } = useAuth()
  const { items, profiles, myStatuses, loading } = useCrewActivity()
  const { setEventInterest } = useUserEventInterests(signedInUid)
  const openEvent = useOpenEvent()
  const { count, sentinelRef } = useIncrementalReveal(items.length, { batchSize: 8 })
  const shown = items.slice(0, count)
  // Seen once the feed is actually on screen this visit — it sits below the
  // inbox, groups and friends, so opening /social alone doesn't count.
  const tabActive = useTabActive()
  const [viewed, setViewed] = useVisitState(false)
  const viewRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = viewRef.current
    if (!el || viewed || !tabActive) return
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setViewed(true)
    })
    io.observe(el)
    return () => io.disconnect()
  }, [viewed, tabActive, setViewed])
  const { seenAtVisitStart } = useCrewActivityUnread(items, loading, tabActive && viewed)

  return (
    <Section title={fbt('What’s new with your crew', 'Crew tab section: recent plans of your friends and groups')}>
      <div ref={viewRef} className="-mb-3" />
      {loading ? (
        <ul className="flex flex-col gap-3">
          {placeholders(3).map((_, i) => (
            <li key={i} className="flex items-center gap-3">
              <Skeleton className="size-10 rounded-md" />
              <Skeleton className="h-4 flex-1" />
            </li>
          ))}
        </ul>
      ) : items.length === 0 ? (
        <EmptyState>
          {fbt('When your friends and groups make plans, they show up here.', 'Empty state of the Crew activity feed')}
        </EmptyState>
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {shown.map(item => (
              <CrewActivityRow
                key={item.key}
                item={item}
                profiles={profiles}
                myStatus={myStatuses.get(item.event.id) ?? 'none'}
                unread={seenAtVisitStart !== null && item.at > seenAtVisitStart}
                onOpen={() => openEvent(item.event, myStatuses.get(item.event.id))}
                onSetStatus={next => setEventInterest(item.event.id, next)}
              />
            ))}
          </ul>
          <div ref={sentinelRef} />
        </>
      )}
    </Section>
  )
}
